// Runtime match state.
// - Multi-weapon inventory (rifle/smg/pistol) with per-weapon ammo & reload.
// - Kept separate from menu state so the MatchScreen can mount/unmount cleanly.
import { create } from "zustand";
import { WEAPONS, WEAPON_ORDER, DEFAULT_WEAPON, getWeapon } from "@/game/weapons/weapons";
import { getAgent } from "@/game/agents/agents";
import { useSettings } from "@/stores/settingsStore";

function getAgentPassive() {
    const id = useSettings.getState().selectedAgent || "recruit";
    return getAgent(id).passive || {};
}

function buildInventory() {
    const inv = {};
    for (const id of WEAPON_ORDER) {
        const w = WEAPONS[id];
        inv[id] = {
            ammo: w.magSize,
            reserve: w.reserve,
            magSize: w.magSize,
            reloading: false,
        };
    }
    return inv;
}

export const useMatchStore = create((set, get) => ({
    // Config
    mapId: null,
    teamSize: "1v1",
    difficulty: "normal",
    // New: match rule ("round" | "rapid" | "solo_drill")
    rule: "round",
    // Rapid Assault settings
    timeLimitSeconds: 600,
    scoreLimit: 50,
    // Environment
    weather: "clear",
    timeOfDay: "day",

    // Score
    roundsToWin: 5,
    roundTimeSeconds: 90,
    scoreYou: 0,
    scoreEnemy: 0,
    round: 1,

    // Player
    hp: 100,
    armor: 100,
    dead: false,

    // Weapons
    inventory: buildInventory(),
    activeWeapon: DEFAULT_WEAPON,
    weaponSwitching: false,

    // Session-wide totals
    kills: 0,
    deaths: 0,

    // Flow
    phase: "playing",
    lastRoundResult: null,
    winner: null,
    matchStartAt: 0,

    killFeed: [],

    setConfig: (cfg) => {
        const {
            mapId,
            teamSize,
            difficulty,
            rule = "round",
            roundsToWin = 5,
            timeLimitSeconds = 600,
            scoreLimit = 50,
            weather = "clear",
            timeOfDay = "day",
        } = cfg || {};
        const startAt = performance.now();
        const passive = getAgentPassive();
        const maxHp = passive.maxHp ?? 100;
        set({
            mapId,
            teamSize,
            difficulty,
            rule,
            roundsToWin,
            timeLimitSeconds,
            scoreLimit,
            weather,
            timeOfDay,
            scoreYou: 0,
            scoreEnemy: 0,
            round: 1,
            hp: maxHp,
            armor: 100,
            maxHp,
            dead: false,
            inventory: buildInventory(),
            activeWeapon: DEFAULT_WEAPON,
            weaponSwitching: false,
            kills: 0,
            deaths: 0,
            phase: "playing",
            lastRoundResult: null,
            winner: null,
            matchStartAt: startAt,
            killFeed: [],
        });
    },

    setPhase: (phase) => set({ phase }),
    setHp: (hp) => set({ hp: Math.max(0, Math.min(100, hp)) }),
    setArmor: (armor) => set({ armor: Math.max(0, Math.min(100, armor)) }),

    damagePlayer: (raw) => {
        const s = get();
        if (s.dead || s.phase !== "playing") return;
        let dmg = raw;
        let armor = s.armor;
        if (armor > 0) {
            const absorbed = Math.min(armor, dmg * 0.5);
            armor -= absorbed;
            dmg -= absorbed;
        }
        const maxHp = s.maxHp ?? 100;
        const hp = Math.max(0, Math.min(maxHp, s.hp - dmg));
        set({ hp, armor });
        if (hp <= 0) get().onPlayerDeath();
    },

    onPlayerDeath: () => {
        const s = get();
        set({ dead: true, deaths: s.deaths + 1 });
        get().pushKill("BOT", "YOU");
        get().endRound("lost");
    },

    onBotKilled: (botName) => {
        const s = get();
        set({ kills: s.kills + 1 });
        get().pushKill("YOU", botName || "BOT");
    },

    pushKill: (killer, victim) => {
        const s = get();
        const w = getWeapon(s.activeWeapon);
        const short = w.id.toUpperCase();
        const item = { id: Math.random().toString(36).slice(2, 8), killer, victim, weapon: short, ts: Date.now() };
        set({ killFeed: [item, ...s.killFeed].slice(0, 5) });
    },

    endRound: (result) => {
        const s = get();
        if (s.phase !== "playing") return;
        const scoreYou = s.scoreYou + (result === "won" ? 1 : 0);
        const scoreEnemy = s.scoreEnemy + (result === "lost" ? 1 : 0);
        let phase = "round_end";
        let winner = null;
        if (scoreYou >= s.roundsToWin) {
            phase = "match_end";
            winner = "you";
        } else if (scoreEnemy >= s.roundsToWin) {
            phase = "match_end";
            winner = "enemy";
        }
        set({ scoreYou, scoreEnemy, phase, lastRoundResult: result, winner });
    },

    nextRound: () => {
        const s = get();
        const maxHp = getAgentPassive().maxHp ?? 100;
        set({
            round: s.round + 1,
            hp: maxHp,
            armor: 100,
            maxHp,
            inventory: buildInventory(),
            activeWeapon: DEFAULT_WEAPON,
            dead: false,
            phase: "playing",
            lastRoundResult: null,
        });
    },

    // ---- Weapons ----
    switchWeapon: (id) => {
        const s = get();
        if (s.dead || s.phase !== "playing") return;
        if (!s.inventory[id]) return;
        if (s.activeWeapon === id) return;
        // Cancel any in-progress reload; start switch animation
        const inv = { ...s.inventory };
        for (const k of Object.keys(inv)) inv[k] = { ...inv[k], reloading: false };
        set({ activeWeapon: id, inventory: inv, weaponSwitching: true });
        // Auto-clear switching flag shortly after (viewmodel handles animation timing)
        setTimeout(() => set({ weaponSwitching: false }), 260);
    },

    // Called by Weapon.jsx when a shot is fired
    fireShot: () => {
        const s = get();
        if (s.dead || s.phase !== "playing") return false;
        const active = s.activeWeapon;
        const w = s.inventory[active];
        if (!w || w.reloading || w.ammo <= 0) return false;
        const inv = { ...s.inventory, [active]: { ...w, ammo: w.ammo - 1 } };
        set({ inventory: inv });
        return true;
    },

    startReload: () => {
        const s = get();
        if (s.dead) return;
        const active = s.activeWeapon;
        const w = s.inventory[active];
        if (!w || w.reloading) return;
        if (w.ammo === w.magSize) return;
        if (w.reserve <= 0) return;
        const inv = { ...s.inventory, [active]: { ...w, reloading: true } };
        set({ inventory: inv });
    },

    finishReload: () => {
        const s = get();
        const active = s.activeWeapon;
        const w = s.inventory[active];
        if (!w || !w.reloading) return;
        const need = w.magSize - w.ammo;
        const take = Math.min(need, w.reserve);
        const inv = {
            ...s.inventory,
            [active]: { ...w, ammo: w.ammo + take, reserve: w.reserve - take, reloading: false },
        };
        set({ inventory: inv });
    },

    togglePause: () => {
        const s = get();
        if (s.phase === "playing") set({ phase: "paused" });
        else if (s.phase === "paused") set({ phase: "playing" });
    },
}));

// ---- Selector helpers ----
export function selectActiveWeaponConfig(state) {
    return getWeapon(state.activeWeapon);
}

export function selectActiveWeaponInventory(state) {
    return state.inventory[state.activeWeapon];
}
